import { Table, Thead, Th, Tr, Tbody, Box, Flex } from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  SortingState,
  Row,
  FilterFn,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useCallback, useState, useEffect } from 'react';

import {
  CalculationTerminologiesMenuFilter,
  CalculationTerminologiesSearchBar,
  CalculationTerminologiesTableRow,
} from './CalculationTerminologiesTableComponents';
import {
  CalcTermsCategory,
  CalcTermsRow,
  calcTermsTableColumnDefs,
  createColumnVisibility,
  termKeyColumnId,
} from './calculationTerminologiesUtils';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';

type Props = {
  calculationTerminologiesTableRows: CalcTermsRow[];
};
export const CalculationTerminologiesTable = ({
  calculationTerminologiesTableRows,
}: Props) => {
  const columnVisibility = createColumnVisibility();
  const [data, setData] = useState<CalcTermsRow[]>(
    calculationTerminologiesTableRows,
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedTermsCategories, setSelectedTermsCategories] = useState<
    Set<CalcTermsCategory>
  >(
    new Set([
      'borrowingBaseCash',
      'borrowingBaseReceivables',
      'borrowingBaseReport',
      'metrics',
      'configuration',
    ]),
  );

  useEffect(() => {
    const filteredData = calculationTerminologiesTableRows.filter(
      (row) =>
        !!_.intersection(row.categories, Array.from(selectedTermsCategories))
          .length,
    );

    setData(filteredData);
  }, [selectedTermsCategories, calculationTerminologiesTableRows]);

  const tableGlobalFilter: FilterFn<CalcTermsRow> = useCallback(
    (row: Row<CalcTermsRow>, columnId: string, query: string) => {
      const searchTerm = String(query).toLowerCase();
      const searchTermNoSpaces = _.camelCase(searchTerm).toLowerCase(); // the cell values of the first "Terms" column does not have spaces
      const rowValue = row.getValue(columnId);
      const rowValueLowerCase = String(rowValue).toLowerCase();

      return rowValue !== undefined
        ? rowValueLowerCase.includes(searchTerm) ||
            rowValueLowerCase.includes(searchTermNoSpaces)
        : false;
    },
    [],
  );

  const table = useReactTable({
    data,
    columns: calcTermsTableColumnDefs,
    state: {
      columnVisibility,
      sorting,
      globalFilter,
    },
    globalFilterFn: tableGlobalFilter,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableGlobalFilter: true,
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  return (
    <>
      <CalculationTerminologiesSearchBar
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <Box
        mt="24px"
        borderWidth="1px"
        borderColor={colors.gray[200]}
        overflowX="auto"
        overflowY="auto"
        position="relative"
      >
        <Table
          style={{ borderCollapse: 'separate', borderSpacing: 0 }}
          minHeight="400px"
        >
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnCanSort = header.column.getCanSort();
                  return (
                    <Th
                      key={header.id}
                      textTransform="capitalize"
                      letterSpacing="normal"
                      padding="8px 16px 8px 16px"
                      position="sticky"
                      top="0"
                      borderBottomWidth="thin"
                      whiteSpace="nowrap"
                      width="33%"
                      backgroundColor={colors.background[100]}
                    >
                      {header.isPlaceholder ? null : (
                        <Flex alignItems="center">
                          {header.id === termKeyColumnId && (
                            <Box
                              cursor={columnCanSort ? 'pointer' : 'default'}
                              onClick={header.column.getToggleSortingHandler()}
                              mr="8px"
                            >
                              {{
                                asc: <Icon icon="sort-up" />,
                                desc: <Icon icon="sort-down" />,
                              }[header.column.getIsSorted() as string] ??
                                (columnCanSort ? <Icon icon="sort" /> : null)}
                            </Box>
                          )}

                          <Box mr="8px">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </Box>
                          {header.id === termKeyColumnId && (
                            <CalculationTerminologiesMenuFilter
                              selectedTermsCategories={selectedTermsCategories}
                              setSelectedTermsCategories={
                                setSelectedTermsCategories
                              }
                            />
                          )}
                        </Flex>
                      )}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody backgroundColor="white">
            {table.getRowModel().rows.map((row) => {
              const termKey: string = row.getValue(termKeyColumnId);

              return (
                <CalculationTerminologiesTableRow
                  key={`calculation-terminologies-row-${termKey}`}
                  row={row}
                />
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </>
  );
};
